import React, { useState } from "react";
import * as IO from "fp-ts/IO";

import DomainPracticalIcon from "src/assets/images/domain-practical.png";
import DomainHeartIcon from "src/assets/images/domain-heart.png";
import DomainIntellectualIcon from "src/assets/images/domain-intellectual.png";
import DomainNurtureIcon from "src/assets/images/domain-nurture.png";
import DomainHandsIcon from "src/assets/images/domain-hands.png";
import DomainHeadIcon from "src/assets/images/domain-head.png";
import img from "src/assets/images/ffi_report_logo.svg";
import { Button } from "src/components/button/button";
import { Modal } from "src/components/modal/modal";
import { Separator } from "src/components/separator/separator";
import { Body, PageTitle, SectionTitle } from "src/components/typography/typography";
import { School } from "src/generated/types";
import { FFIBrandedV1Report } from "src/pages/reports/ffi/v1/types";
import { useDocumentTitle } from "src/utils/use_document_title";
import ffiModel from "src/assets/images/ffi_model.png";

import { Intellectual } from "./sections/intellectual/intellectual";
import { Nurture } from "./sections/nurture/nurture";
import { Practical } from "./sections/practical/practical";
import { Heart } from "./sections/heart/heart";
import { MeaningfulStrengths } from "./sections/meaningful_strengths/meaningful_strengths";
import { KeyGrowthOpportunities } from "./sections/key_growth_opportunities/key_growth_opportunities";
import { Hands } from "./sections/hands/hands";
import { Head } from "./sections/head/head";

/*
 * Types
 */

interface ReportFullPageProps {
    readonly report: FFIBrandedV1Report;
    readonly school: School;
}

/*
 * Main
 */

export const ReportFullPage = (props: ReportFullPageProps): React.ReactElement => {
    const { report, school } = props;

    useDocumentTitle("Flourishing Faith Index");

    const [isModalOpen, setModalOpen] = useState<boolean>(true);

    const convertToPdf: IO.IO<void> = () => window.print();

    return (
        <div className="main-page-wrap report full-report">
            <div className="center">
                <div className="report-page-top">
                    <div className="report-page-title-line">
                        <img alt="ACSI Flourishing Faith Index" className="ffi-report-image" src={img} />
                        <PageTitle>{school.name}</PageTitle>
                    </div>
                    <Separator />
                </div>

                <div className="prepended-page-wrap">
                    <div className="report-additional-page page-0">
                        <div className="center">
                            <img alt="ACSI FFI" className="fsci-report-printable-image" src={img} />
                            <PageTitle className="fsci-report-title">
                                Flourishing Faith Index
                                <br />
                                {school && <span>{`For ${school.name}`}</span>}
                            </PageTitle>
                        </div>
                    </div>
                    <div className="report-additional-page page-1">
                        <div className="center">
                            <SectionTitle className="green">Introduction</SectionTitle>
                            <Body>
                                Thank you for your school’s participation in the Flourishing Faith Index (FFI). ACSI is
                                pleased to partner with committed schools like yours across the country to collect and
                                leverage empirical data in the pursuit of insight around how Christian schools can
                                support spiritual formation and biblical worldview development in students.
                            </Body>
                            <Body>
                                The first administration of the FFI tested 799 discrete items across seven different
                                survey groups: students; parents; alumni; teachers; leaders / administrators; support
                                staff; and board members. In Fall 2022, nearly 10,000 survey responses were collected
                                from Christian schools of diverse size and geographic location, thereby making the FFI
                                the largest study of spiritual formation and biblical worldview development in Christian
                                schools to date. In subsequent administration, with Christian schools in both the United
                                States and around the world, we plan to build upon the insights around spiritual
                                formation in Christian schools to deliver validated and relevant insights.
                            </Body>
                            <Body>
                                The validated constructs identified through FFI data analysis were mapped onto a
                                research-based model of spiritual formation in Christian schools—the Flourishing Faith
                                Model (FFM). The FFM clusters the validated constructs for all seven survey groups into
                                a two-tiered model of six domains.
                            </Body>
                            <Body>
                                In the inner core, we organize measures of flourishing faith in three domains: Head,
                                Heart, and Hands. The Head captures respondents’ intellectual assent to 25 statements
                                based on the ACSI Statement of Faith. Hands measures respondents’ self-reported
                                frequency of engaging in ten spiritual disciplines. The Heart includes validated
                                measures assessing respondents’ heart disposition towards the Christian faith.
                            </Body>
                            <Body>
                                In the outer spiritual formation ring, we organize the remaining validated constructs
                                into three domains: Intellectual, Nurture, and Practical. These domains include
                                validated measures of spiritual formation practices in which the school and surrounding
                                community engage in order to support students’ spiritual 2 formation. While we theorize
                                each spiritual formation domain to correlate most strongly with its corresponding
                                flourishing faith domain—Intellectual with Head, Nurture with Heart, and Practical with
                                Hands—we also firmly believe that spiritual formation is an interconnected and complex
                                process. Intellectual assent to claims about Christianity (e.g., “The Bible is the
                                inspired Word of God”) can and should coincide with changes in the Heart (e.g., “I hope
                                to grow more faithful in applying the Bible to my life”) and Hands (“I read the Bible
                                every day”).
                            </Body>
                            <Body>
                                All of your school’s data has been handled with the utmost care to ensure full
                                confidentiality. Your school’s profile, based on the results of the FFI as mapped on to
                                the FFM, is designed to highlight areas of existing strength as well as opportunities
                                for growth and improvement. Insights from the FFI have the potential to inform
                                programmatic priorities, professional development, curricular review, strategic planning
                                and budgeting, communication efforts, and much more.
                            </Body>
                            <Body>
                                As with most institutional survey research, it is important to note that FFI findings
                                are best used when they are interpreted in light of school constituencies’ lived
                                experiences as collected through qualitative means. This means that schools will benefit
                                the most fully from this report when data is used to generate reflection, dialogue, and
                                collaborative planning for the present and the future. Please be sure to review the
                                report section entitled “Responding to Your School’s Results,” on the last two pages of
                                the report, for suggestions in this regard.
                            </Body>
                            <Body>
                                Thank you again for your school’s participation in the FFI. We pray the insights in this
                                report will help your school to continue to grow, and that ultimately your school may be
                                a faithful instrument in the Redeemer’s hands “to tell the coming generation the
                                glorious deeds of the LORD, and his might, and the wonders that he has done” (Psalm
                                78:4).
                            </Body>
                            <Body>
                                For questions regarding this report, please contact{" "}
                                <a href="mailto:assessmentsupport@acsi.org">assessmentsupport@acsi.org</a>.
                            </Body>
                        </div>
                    </div>
                    <div className="report-additional-page page-2">
                        <div className="center">
                            <SectionTitle className="green">Overview of the Flourishing Faith Model</SectionTitle>
                            <Body>
                                Analysis of the FFI identified 27 validated constructs related to spiritual formation
                                across the seven groups of participants. These constructs have been grouped into a model
                                with four major domains, along with a propositional Head survey and a spiritual
                                disciplines Hand survey. In the following pages, a definitional overview will be
                                provided for these domains, followed by a listing of specific constructs that make up
                                each domain.
                            </Body>
                            <div className="model-image-wrap report-page">
                                <img alt="FFI Model" src={ffiModel} />
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-3">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="green">
                                            Defining <em>Intellectual</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        The Christian faith is one that can be known (Luke 1:1-4; 1 John 5:13). Faith is
                                        formed at least in part in the mind and the Bible often describes faith as
                                        belief (Mark 1:15; John 20:31; 2 Thessalonians 2:13). Thus, Christian schools
                                        seek to train students’ minds and to prepare them to view the world from a
                                        biblical perspective.
                                    </Body>
                                    <Body>
                                        A Christian school engages in many intellectual practices to train minds and
                                        develop a biblical worldview. FFI data analysis identified constructs related to
                                        the Intellectual domain for six of the seven constituent groups (students;
                                        alumni; teachers; leaders/administrators; support staff; and board members),
                                        below.
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="intellecutual" src={DomainIntellectualIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                            <div className="construct-wrapper">
                                <SectionTitle className="green">INTELLECTUAL CONSTRUCTS</SectionTitle>
                                <Body>
                                    <strong>Teacher Worldview Training </strong> – The school trains teachers in their
                                    biblical worldview and evaluates their growth (Ezra 7:10; Romans 12:1-2; Colossians
                                    3:10-11).
                                </Body>
                                <Body>
                                    <strong>Integrating Faith and Learning</strong> – Students see how their academic
                                    work helps them better understand God’s character and the Bible’s teachings
                                    (Philippians 2:5; 1 Corinthians 2:14-16; Romans 8:6-7; Matthew 12:25, 30).
                                </Body>
                                <Body>
                                    <strong>Faith-Based Accreditation</strong> – Board members see the value of the
                                    accreditation process in aligning the school with its Christian mission (Philippians
                                    1:10; 1 Timothy 4:15; Colossians 3:10-11, 23; Hebrews 11:1; Luke 18:27).
                                </Body>
                                <Body>
                                    <strong>Integrating Faith and Teaching</strong> –Teachers study how to integrate
                                    faith with their academic discipline and receive appropriate training (Romans
                                    12:1-2; 1 Corinthians 11:1; 1 John 2:6).
                                </Body>
                                <Body>
                                    <strong>Spiritual Guidance</strong> – School personnel are open to guiding students
                                    with their questions and doubts about the Christian faith, and alumni recall the
                                    school providing a safe environment for questions (Acts 8:26-40; Luke 24:38; Jude
                                    22).
                                </Body>
                                <Body>
                                    <strong>Distinctively Christian Education</strong> – Board members feel Christian
                                    education must be distinct in both content and pedagogy (2 Timothy 1:13, 3:16-17).
                                </Body>
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-4">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="green">
                                            Defining <em>Nurture</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        Faith extends to the heart. Belief in Christ is evidence of true heart change
                                        (Romans 10:9-10) and the product of divine heart surgery (Ezekiel 36:26-27).
                                        True believers do not simply assent to certain truths asserted by the Bible;
                                        their desires change to seek after the things of God (Galatians 2:20; 1 John
                                        3:19-24).
                                    </Body>
                                    <Body>
                                        A Christian school works closely with the local church and with parents to
                                        nurture a certain heart disposition towards the Christian faith. For many
                                        schools, nurturing spiritual formation emerges from a partnership with church
                                        and family and yields close relationships between students, leaders, and
                                        families (Deuteronomy 6; Joshua 24; Psalms 78, 127). At the same time, it is
                                        important to note that covenantal and missional schools may interpret some of
                                        this data differently, while still providing important insights to both types of
                                        schools into the nature of the school’s relationships with its families and
                                        parents. FFI data analysis identified constructs related to the Nurture domain
                                        for six of the seven constituent groups (students; parents; alumni; teachers;
                                        leaders/administrators; and support staff), below.
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="nurture" src={DomainNurtureIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                            <div className="construct-wrapper">
                                <SectionTitle className="green">NURTURE CONSTRUCTS</SectionTitle>
                                <Body>
                                    <strong>Spiritual Growth</strong> – Administrators, teachers, and staff feel
                                    supported in their spiritual health and growth (2 Peter 3:18; Colossians 2:6-7;
                                    Psalm 92:12-14).
                                </Body>
                                <Body>
                                    <strong>Mentoring Leaders</strong> – Administrators have been mentored by other
                                    leaders and embody servant leadership by mentoring others (1 Corinthians 4:17;
                                    Philippians 2:22; 1 Timothy 1:2).
                                </Body>
                                <Body>
                                    <strong>Supportive Culture</strong> – Alumni recall being supported by members of
                                    the school community (Galatians 6:2; Colossians 3:13).
                                </Body>
                                <Body>
                                    <strong>Faithful Friends</strong> – Alumni report maintaining close friendships with
                                    friends from school, who continue to be strong in their faith (Proverbs 18:24).
                                </Body>
                                <Body>
                                    <strong>Peer Encouragement</strong> – As “iron sharpens iron,” students feel
                                    supported by their peers in the Christian faith (Proverbs 27:17).
                                </Body>
                                <Body>
                                    <strong>Parental Encouragement</strong> – Parents and students spend quality time
                                    with each other and engage in conversation about the Christian faith (Deuteronomy
                                    6:7; Psalm 78:4).
                                </Body>
                                <Body>
                                    <strong>Parent Partnership</strong> – Administrators understand the importance of a
                                    strong partnership between parents and the school for nurturing the child’s
                                    spiritual growth (Deuteronomy 6:7; Joshua 24:15; Psalms 78, 127).
                                </Body>
                                <Body>
                                    <strong>Church-School Congruence</strong> – Students recognize how the teachings of
                                    their school and church agree with each other (Romans 12:4-5; Hebrews 10:25).
                                </Body>
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-5">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="green">
                                            Defining <em>Practical</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        Faith is lived out in the hands. God writes His law upon our hearts and causes
                                        us to walk according to His statutes (Jeremiah 31:31-34). Christian schools put
                                        policies in place and engage in practices that foster spiritual disciplines in
                                        all members of the school community.
                                    </Body>
                                    <Body>
                                        Such practices may be implemented at the school level, such as a policy that
                                        promotes Sabbath-keeping or the ways in which the board helps guard the mission.
                                        They may take place in the classroom, as when a school provides practical skills
                                        for evaluating the world. They may also exist in the family, as when parents
                                        read the Bible with their children or protect them from potentially harmful
                                        content. FFI data analysis identified constructs related to the Practical domain
                                        for all seven constituent groups (students; parents; alumni; teachers;
                                        leaders/administrators; and support staff), below.
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="practical" src={DomainPracticalIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                            <div className="construct-wrapper">
                                <SectionTitle className="green">PRACTICAL CONSTRUCTS</SectionTitle>
                                <Body>
                                    <strong>Guarding the Mission</strong> – Board members feel confident that the school
                                    will guard its mission (1 Timothy 6:20).
                                </Body>
                                <Body>
                                    <strong>Missional Hiring</strong> – The school hires faculty and staff who are
                                    rooted in their faith and support the school’s beliefs and mission (Amos 3:3; 2
                                    Corinthians 6:14).
                                </Body>
                                <Body>
                                    <strong>Special Education Inclusion</strong> – The school’s special education
                                    practices welcome students with special needs and honor them as image bearers (Psalm
                                    139:13-16; Mark 10:14; John 9:1-3).
                                </Body>
                                <Body>
                                    <strong>Biblical Perspectives</strong> – From the perspective of alumni, the school
                                    provided students opportunities to engage with biblical perspectives on social and
                                    political issues (Romans 12:2; Ephesians 2:1-2; Colossians 2:8).
                                </Body>
                                <Body>
                                    <strong>Equipping for Good Work</strong> – Administrators and staff believe the
                                    school provides students with practical skills for evaluating the world and
                                    stewarding their health and financial resources (Proverbs 22:6; Luke 6:40; 1 Timothy
                                    4:8; 2 Timothy 3:16).
                                </Body>
                                <Body>
                                    <strong>Sabbath-Keeping</strong> – The school has policies to help personnel and
                                    families keep the Sabbath and communicates these expectations (Exodus 20:8-11;
                                    Deuteronomy 5:12-15; Hebrews 4:1-13).
                                </Body>
                                <Body>
                                    <strong>Family Devotions</strong> – Parents and students report spending time in
                                    their families reading and praying together (Deuteronomy 6:7; Joshua 24:15; Psalms
                                    78, 127).
                                </Body>
                                <Body>
                                    <strong>Parental Protection</strong> – Parents provide a healthy “hedge of
                                    protection” for their children from potentially harmful content (Job 1:10).
                                </Body>
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-6">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="green">
                                            Defining <em>Heart</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        Of all the measures of flourishing faith, the Heart proves the most difficult to
                                        measure. External fruit such as spiritual disciplines can be observed and
                                        measured. Head knowledge can be assessed. But if “faith is the assurance of
                                        things hoped for, the conviction of things not yet seen” (Hebrews 11:1), as the
                                        evidence of things unseen, we can, however imperfectly, measure the growth of
                                        faith in our own lives as we seek to live out our Christian faith more and more
                                        faithfully.
                                    </Body>
                                    <Body>
                                        The FFI identified respondents’ heart disposition towards God, the local church,
                                        the Bible, others, and the future. The constructs related to Heart were
                                        identified for six of the seven survey groups (students; parents; alumni;
                                        teachers; leaders/administrators; and support staff), below.
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="heart" src={DomainHeartIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                            <div className="construct-wrapper">
                                <SectionTitle className="green">HEART CONSTRUCTS</SectionTitle>
                                <Body>
                                    <strong>Church Engagement</strong> – Administrators are deeply devoted to a local
                                    church body (Romans 12:4-5; Hebrews 10:25).
                                </Body>
                                <Body>
                                    <strong>Personal Devotions</strong> – Administrators, teachers, alumni, and staff
                                    are diligent about engaging with the Bible, including reading and memorizing
                                    Scripture (Psalms 19:7-8; 119:15).
                                </Body>
                                <Body>
                                    <strong>Spiritual Maturity</strong> – Parents and teachers desire to grow in
                                    understanding and living according to the Bible (Hebrews 5:12-14, 6:1; John 15:5;
                                    Philippians 1:9).
                                </Body>
                                <Body>
                                    <strong>Hopefulness</strong> – Students and alumni express optimism and
                                    purposefulness about their lives (Job 13:15; Jeremiah 29:11; Romans 5:5).
                                </Body>
                                <Body>
                                    <strong>Reconciliation</strong> – Students seek forgiveness from others and offer
                                    forgiveness when it is asked of them (Matthew 6:15; Ephesians 4:32; Colossians
                                    3:13).
                                </Body>
                                <Body>
                                    <strong>Reverence</strong> – Administrators, staff, and students revere God and live
                                    their lives in submission to His authority (1 Samuel 12:24; Proverbs 1:7; Hebrews
                                    12:9; 2 Timothy 3:16-17).
                                </Body>
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-7">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="green">
                                            Defining <em>Head</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        While the 25 - item propositional survey, based on ACSI’s Statement of Faith,
                                        was not validated by factor analysis, preliminary analysis lends substantial
                                        face validity to the Head domain: students enrolled in their Christian school
                                        for more years were more likely to assent to survey items than students enrolled
                                        for just a few years, and respondents who self - reported being a Christian “for
                                        many years” were more likely to assent than those who were unsure or knew that
                                        they were not a Christian. See items and scores on p. 24.
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="head" src={DomainHeadIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                        </div>
                    </div>
                    <div className="report-additional-page page-7">
                        <div className="center">
                            <div className="report-split-content">
                                <div className="split-part-left">
                                    <Body>
                                        <strong className="green">
                                            Defining <em>Hands</em>
                                        </strong>
                                    </Body>
                                    <Body>
                                        Like the 25-item propositional survey, the Hands domain does not contain any
                                        validated constructs, but rather ten spiritual disciplines asked across the
                                        seven constituent groups. Best practice in survey design is not to ask
                                        respondents how they feel about a particular item (unavoidable for any measure
                                        of Heart), but rather how often they engage in particular behaviors. See items
                                        and scores for the five student items on p. 29.
                                    </Body>
                                </div>
                                <div className="split-part-right">
                                    <img alt="hands" src={DomainHandsIcon} />
                                </div>
                                <div className="clear" />
                            </div>
                        </div>
                    </div>
                </div>

                <MeaningfulStrengths report={report} />
                <KeyGrowthOpportunities report={report} />
                <Intellectual report={report} />
                <Nurture report={report} />
                <Practical report={report} />
                <Head report={report} />
                <Hands report={report} />
                <Heart report={report} />

                <div className="prepended-page-wrap">
                    <div className="report-additional-page page-8">
                        <div className="center">
                            <SectionTitle className="section-title-extra-spacing green">Results Summary</SectionTitle>

                            <Body className="more-top">
                                <strong>Intellectual</strong>
                                <ol>
                                    <li>
                                        IN01 – Teacher Worldview Training{" "}
                                        <strong>
                                            {Math.round(
                                                Number(
                                                    report.value.results.raw.intellectual.teacherWorldviewTraining.toFixed(
                                                        2,
                                                    ),
                                                ) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        IN02 – Integrating Faith and Teaching{" "}
                                        <strong>
                                            {Math.round(
                                                Number(
                                                    report.value.results.raw.intellectual.integratingFaithAndTeaching.toFixed(
                                                        2,
                                                    ),
                                                ) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        IN03 – Integrating Faith and Learning{" "}
                                        <strong>
                                            {Math.round(
                                                Number(
                                                    report.value.results.raw.intellectual.integratingFaithAndLearning.toFixed(
                                                        2,
                                                    ),
                                                ) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        IN04 – Spiritual Guidance{" "}
                                        <strong>
                                            {Math.round(
                                                Number(
                                                    report.value.results.raw.intellectual.spiritualGuidance.toFixed(2),
                                                ) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        IN05 – Faith-Based Accreditation{" "}
                                        <strong>
                                            {Math.round(
                                                Number(
                                                    report.value.results.raw.intellectual.faithBasedAccreditation.toFixed(
                                                        2,
                                                    ),
                                                ) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        IN06 – Distinctively Christian Education{" "}
                                        <strong>
                                            {Math.round(
                                                Number(
                                                    report.value.results.raw.intellectual.distinctivelyChristianEducation.toFixed(
                                                        2,
                                                    ),
                                                ) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                </ol>
                                <strong>Nurture</strong>
                                <ol>
                                    <li>
                                        NU01 – Spiritual Growth{" "}
                                        <strong>
                                            {Math.round(
                                                Number(report.value.results.raw.nurture.spiritualGrowth.toFixed(2)) *
                                                    100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        NU02 – Mentoring Leaders{" "}
                                        <strong>
                                            {Math.round(
                                                Number(report.value.results.raw.nurture.mentoringLeaders.toFixed(2)) *
                                                    100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        NU03 – Supportive Culture{" "}
                                        <strong>
                                            {Math.round(
                                                Number(report.value.results.raw.nurture.supportiveCulture.toFixed(2)) *
                                                    100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        NU04 – Faithful Friends{" "}
                                        <strong>
                                            {Math.round(
                                                Number(report.value.results.raw.nurture.faithfulFriends.toFixed(2)) *
                                                    100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        NU05 – Peer Encouragement{" "}
                                        <strong>
                                            {Math.round(
                                                Number(report.value.results.raw.nurture.peerEncouragement.toFixed(2)) *
                                                    100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        NU06 – Parental Encouragement{" "}
                                        <strong>
                                            {Math.round(
                                                Number(
                                                    report.value.results.raw.nurture.parentalEncouragement.toFixed(2),
                                                ) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        NU07 – Parent Partnership{" "}
                                        <strong>
                                            {Math.round(
                                                Number(report.value.results.raw.nurture.parentPartnership.toFixed(2)) *
                                                    100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        NU08 – Church School Congruence{" "}
                                        <strong>
                                            {Math.round(
                                                Number(
                                                    report.value.results.raw.nurture.churchSchoolCongruence.toFixed(2),
                                                ) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                </ol>
                                <strong>Practical</strong>
                                <ol>
                                    <li>
                                        PR01 – Guarding The Mission{" "}
                                        <strong>
                                            {Math.round(
                                                Number(
                                                    report.value.results.raw.practical.guardingTheMission.toFixed(2),
                                                ) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        PR02 – Missional Hiring{" "}
                                        <strong>
                                            {Math.round(
                                                Number(report.value.results.raw.practical.missionalHiring.toFixed(2)) *
                                                    100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        PR03 – Special Education Inclusion{" "}
                                        <strong>
                                            {Math.round(
                                                Number(
                                                    report.value.results.raw.practical.specialEducationInclusion.toFixed(
                                                        2,
                                                    ),
                                                ) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        PR04 – Biblical Perspectives{" "}
                                        <strong>
                                            {Math.round(
                                                Number(
                                                    report.value.results.raw.practical.biblicalPerspectives.toFixed(2),
                                                ) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        PR05 – Equipping For Good Work{" "}
                                        <strong>
                                            {Math.round(
                                                Number(
                                                    report.value.results.raw.practical.equippingForGoodWork.toFixed(2),
                                                ) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        PR06 – Sabbath Keeping{" "}
                                        <strong>
                                            {Math.round(
                                                Number(report.value.results.raw.practical.sabbathKeeping.toFixed(2)) *
                                                    100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        PR07 – Family Devotions{" "}
                                        <strong>
                                            {Math.round(
                                                Number(report.value.results.raw.practical.familyDevotions.toFixed(2)) *
                                                    100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        PR08 – Parental Protection{" "}
                                        <strong>
                                            {Math.round(
                                                Number(
                                                    report.value.results.raw.practical.parentalProtection.toFixed(2),
                                                ) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                </ol>
                                <strong>Heart</strong>
                                <ol>
                                    <li>
                                        HT01 – Church Engagement{" "}
                                        <strong>
                                            {Math.round(
                                                Number(report.value.results.raw.heart.churchEngagement.toFixed(2)) *
                                                    100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        HT02 – Personal Devotions{" "}
                                        <strong>
                                            {Math.round(
                                                Number(report.value.results.raw.heart.personalDevotions.toFixed(2)) *
                                                    100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        HT03 – Spiritual Maturity{" "}
                                        <strong>
                                            {Math.round(
                                                Number(report.value.results.raw.heart.spiritualMaturity.toFixed(2)) *
                                                    100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        HT04 – Hopefulness{" "}
                                        <strong>
                                            {Math.round(
                                                Number(report.value.results.raw.heart.hopefulness.toFixed(2)) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        HT05 – Reconciliation{" "}
                                        <strong>
                                            {Math.round(
                                                Number(report.value.results.raw.heart.reconciliation.toFixed(2)) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                    <li>
                                        HT06 – Reverence{" "}
                                        <strong>
                                            {Math.round(
                                                Number(report.value.results.raw.heart.reverence.toFixed(2)) * 100,
                                            )}
                                            %
                                        </strong>
                                    </li>
                                </ol>
                            </Body>
                        </div>
                    </div>
                </div>

                <div className="prepended-page-wrap">
                    <div className="report-additional-page page-8">
                        <div className="center">
                            <>
                                <SectionTitle className="section-title-extra-spacing green">
                                    Submission Size Summary
                                </SectionTitle>
                                <Body className="more-top">
                                    <strong>Students</strong>
                                    <ul>
                                        <li>Constituent group size: {report.value?.constituentGroups.students}</li>
                                        <li>Total number of responses: {report.value?.surveysCompleted.students}</li>
                                        <li>
                                            Total completion percentage:{" "}
                                            {(
                                                (report.value?.surveysCompleted.students /
                                                    report.value?.constituentGroups.students) *
                                                100.0
                                            ).toFixed(1)}
                                            %
                                        </li>
                                    </ul>
                                    <strong>Teachers</strong>
                                    <ul>
                                        <li>Constituent group size: {report.value?.constituentGroups.teachers}</li>
                                        <li>Total number of responses: {report.value?.surveysCompleted.teachers}</li>
                                        <li>
                                            Total completion percentage:{" "}
                                            {(
                                                (report.value?.surveysCompleted.teachers /
                                                    report.value?.constituentGroups.teachers) *
                                                100.0
                                            ).toFixed(1)}
                                            %
                                        </li>
                                    </ul>
                                    <strong>Administrators</strong>
                                    <ul>
                                        <li>
                                            Constituent group size: {report.value?.constituentGroups.administrators}
                                        </li>
                                        <li>
                                            Total number of responses: {report.value?.surveysCompleted.administrators}
                                        </li>
                                        <li>
                                            Total completion percentage:{" "}
                                            {(
                                                (report.value?.surveysCompleted.administrators /
                                                    report.value?.constituentGroups.administrators) *
                                                100.0
                                            ).toFixed(1)}
                                            %
                                        </li>
                                    </ul>
                                    <strong>Support Staff</strong>
                                    <ul>
                                        <li>Constituent group size: {report.value?.constituentGroups.supportStaff}</li>
                                        <li>
                                            Total number of responses: {report.value?.surveysCompleted.supportStaff}
                                        </li>
                                        <li>
                                            Total completion percentage:{" "}
                                            {(
                                                (report.value?.surveysCompleted.supportStaff /
                                                    report.value?.constituentGroups.supportStaff) *
                                                100.0
                                            ).toFixed(1)}
                                            %
                                        </li>
                                    </ul>
                                    <strong>Parents</strong>
                                    <ul>
                                        <li>Constituent group size: {report.value?.constituentGroups.parents}</li>
                                        <li>Total number of responses: {report.value?.surveysCompleted.parents}</li>
                                        <li>
                                            Total completion percentage:{" "}
                                            {(
                                                (report.value?.surveysCompleted.parents /
                                                    report.value?.constituentGroups.parents) *
                                                100.0
                                            ).toFixed(1)}
                                            %
                                        </li>
                                    </ul>
                                    <strong>Alumni</strong>
                                    <ul>
                                        <li>Constituent group size: {report.value?.constituentGroups.alumni}</li>
                                        <li>Total number of responses: {report.value?.surveysCompleted.alumni}</li>
                                        <li>
                                            Total completion percentage:{" "}
                                            {(
                                                (report.value?.surveysCompleted.alumni /
                                                    report.value?.constituentGroups.alumni) *
                                                100.0
                                            ).toFixed(1)}
                                            %
                                        </li>
                                    </ul>
                                    <strong>Board Members</strong>
                                    <ul>
                                        <li>Constituent group size: {report.value?.constituentGroups.boardMembers}</li>
                                        <li>
                                            Total number of responses: {report.value?.surveysCompleted.boardMembers}
                                        </li>
                                        <li>
                                            Total completion percentage:{" "}
                                            {(
                                                (report.value?.surveysCompleted.boardMembers /
                                                    report.value?.constituentGroups.boardMembers) *
                                                100.0
                                            ).toFixed(1)}
                                            %
                                        </li>
                                    </ul>
                                </Body>
                            </>
                        </div>
                    </div>
                </div>
            </div>

            <Modal isOpen={isModalOpen} setOpen={setModalOpen} title="Print instructions">
                <Body>
                    You can either print a copy of your report or save it as a PDF. To save it as a PDF, click print,
                    expand the printer dropdown, and choose "Save as PDF."
                </Body>
                <Button
                    onClick={() => {
                        setModalOpen(false);
                        setTimeout(() => convertToPdf(), 1000);
                    }}
                >
                    Ok
                </Button>
            </Modal>
        </div>
    );
};
